import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  Clock,
  CheckCircle,
  PlayCircle,
  Award,
  Loader2,
  FolderOpen,
  Trophy,
  TrendingUp
} from 'lucide-react';

export default function MyBuilderCourses() {
  const { api } = useAuth();
  const [courses, setCourses] = useState([]);
  const [stats, setStats] = useState({ total: 0, in_progress: 0, completed: 0, not_started: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  const fetchCourses = useCallback(async () => {
    try {
      setLoading(true);
      const statusParam = activeTab !== 'all' ? `?status=${activeTab}` : '';
      const response = await api.get(`/finance-training/builder-courses/my-courses${statusParam}`);
      setCourses(response.data.courses || []);
      setStats(response.data.stats || { total: 0, in_progress: 0, completed: 0, not_started: 0 });
    } catch (error) {
      console.error('Failed to fetch courses:', error);
      toast.error('Failed to load your courses');
    } finally {
      setLoading(false);
    }
  }, [api, activeTab]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-emerald-500">Completed</Badge>;
      case 'in_progress':
        return <Badge className="bg-blue-500">In Progress</Badge>;
      case 'not_started':
        return <Badge variant="secondary">Not Started</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="my-builder-courses">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">My Courses</h1>
            <p className="text-muted-foreground mt-1">
              Track your learning progress
            </p>
          </div>
          <Button asChild variant="outline">
            <Link to="/finance-training">
              <BookOpen className="h-4 w-4 mr-2" />
              Browse All Courses
            </Link>
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <BookOpen className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-xs text-muted-foreground">Total Enrolled</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <TrendingUp className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.in_progress}</p>
                  <p className="text-xs text-muted-foreground">In Progress</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <CheckCircle className="h-5 w-5 text-emerald-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.completed}</p>
                  <p className="text-xs text-muted-foreground">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/10">
                  <Trophy className="h-5 w-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.completed}</p>
                  <p className="text-xs text-muted-foreground">Certificates</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="all">All ({stats.total})</TabsTrigger>
            <TabsTrigger value="in_progress">In Progress ({stats.in_progress})</TabsTrigger>
            <TabsTrigger value="completed">Completed ({stats.completed})</TabsTrigger>
            <TabsTrigger value="not_started">Not Started ({stats.not_started})</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Course List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : courses.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <FolderOpen className="h-16 w-16 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">No courses found</h3>
              <p className="text-muted-foreground text-center mt-1">
                {activeTab === 'all' 
                  ? "You haven't been enrolled in any courses yet."
                  : `No courses with "${activeTab.replace('_', ' ')}" status.`
                }
              </p>
              <Button asChild className="mt-4">
                <Link to="/finance-training">Browse Courses</Link>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {courses.map(course => (
              <Card 
                key={course.id} 
                className="hover:shadow-md transition-shadow"
                data-testid={`course-card-${course.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg truncate">{course.title}</CardTitle>
                      <CardDescription className="line-clamp-2 mt-1">
                        {course.description || 'No description'}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex items-center gap-2 mb-3">
                    {getStatusBadge(course.user_progress?.status || 'not_started')}
                    <Badge variant="outline" className="text-xs">
                      {course.category_name || 'Uncategorized'}
                    </Badge>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Progress</span>
                      <span className="font-medium">
                        {course.user_progress?.overall_progress_percent || 0}%
                      </span>
                    </div>
                    <Progress 
                      value={course.user_progress?.overall_progress_percent || 0} 
                      className="h-2" 
                    />
                  </div>

                  {/* Stats */}
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-4">
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {course.estimated_hours || 0}h
                    </div>
                    <div className="flex items-center gap-1">
                      <BookOpen className="h-3 w-3" />
                      {course.module_count || 0} modules
                    </div>
                    {course.user_progress?.overall_score > 0 && (
                      <div className="flex items-center gap-1">
                        <Award className="h-3 w-3" />
                        {course.user_progress.overall_score}%
                      </div>
                    )}
                  </div>

                  {/* Action Button */}
                  {course.user_progress?.certificate_id ? (
                    <div className="space-y-2">
                      <Button asChild className="w-full" variant="outline">
                        <Link to={`/finance-training/builder-courses/${course.id}/certificate`}>
                          <Award className="h-4 w-4 mr-2" />
                          View Certificate
                        </Link>
                      </Button>
                      <Button asChild className="w-full" variant="ghost">
                        <Link to={`/finance-training/builder-courses/${course.id}`}>
                          Review Course
                        </Link>
                      </Button>
                    </div>
                  ) : (
                    <Button asChild className="w-full">
                      <Link to={`/finance-training/builder-courses/${course.id}`}>
                        {course.user_progress?.status === 'in_progress' ? (
                          <>
                            <PlayCircle className="h-4 w-4 mr-2" />
                            Continue Learning
                          </>
                        ) : (
                          <>
                            <PlayCircle className="h-4 w-4 mr-2" />
                            Start Course
                          </>
                        )}
                      </Link>
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  );
}
